import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { DEFAULT_CATEGORY_OPTIONS } from "./config";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import i18n from "@/i18n";

const filterA9TransactionsSchema = z.object({
  a9_portfolio_client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a9_portfolio_vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a9_portfolio_account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a9_portfolio_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a9_portfolio_bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  actif_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  category_contains: z.string().optional(),
  transfer_contains: z.string().optional(),
  fee_type_contains: z.string().optional(),
  description_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  a9_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
});

export type FilterA9TransactionsSchema = z.infer<
  typeof filterA9TransactionsSchema
>;

const portfolioDepFields: readonly DepField<FilterA9TransactionsSchema>[] = [
  {
    name: "a9_portfolio_bank_name_contains",
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "a9_portfolio_client_contains",
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "a9_portfolio_vehicle_contains",
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "a9_portfolio_name_contains",
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: "a9_portfolio_account_name_contains",
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

const fields: Field[] = [
  {
    name: "a9_portfolio_client_contains",
    label: `Client`,
    type: "asyncAutocomplete",
    base_url: "a9/a9_portfolios",
    column_name: "client",
    filters: makeFiltersBuilder<FilterA9TransactionsSchema>(
      portfolioDepFields,
      "a9_portfolio_client_contains",
    ),
  },
  {
    name: "a9_portfolio_vehicle_contains",
    label: `Véhicule / Bénéficiaire`,
    type: "asyncAutocomplete",
    base_url: "a9/a9_portfolios",
    column_name: "vehicle",
    filters: makeFiltersBuilder<FilterA9TransactionsSchema>(
      portfolioDepFields,
      "a9_portfolio_vehicle_contains",
    ),
  },
  {
    name: "a9_portfolio_account_name_contains",
    label: `Groupe de compte`,
    type: "asyncAutocomplete",
    base_url: "a9/a9_portfolios",
    column_name: "account_name",
    filters: makeFiltersBuilder<FilterA9TransactionsSchema>(
      portfolioDepFields,
      "a9_portfolio_account_name_contains",
    ),
  },
  {
    name: "a9_portfolio_name_contains",
    label: `Compte`,
    type: "asyncAutocomplete",
    base_url: "a9/a9_portfolios",
    column_name: "name",
    filters: makeFiltersBuilder<FilterA9TransactionsSchema>(
      portfolioDepFields,
      "a9_portfolio_name_contains",
    ),
  },
  {
    name: "a9_portfolio_bank_name_contains",
    label: `Banque`,
    type: "asyncAutocomplete",
    base_url: "a9/a9_portfolios",
    column_name: "bank_name",
    filters: makeFiltersBuilder<FilterA9TransactionsSchema>(
      portfolioDepFields,
      "a9_portfolio_bank_name_contains",
    ),
  },
  {
    name: "actif_contains",
    label: `Actif`,
    type: "asyncAutocomplete",
    base_url: "a9/a9_static_data",
    column_name: "name",
  },
  {
    name: "category_contains",
    label: `Type de mouvement`,
    type: "sectionSelect",
    useTranslation: true,
    translate_key: "category",
    client: "a9",
    table_name: "a9_transactions",
    language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
    options: DEFAULT_CATEGORY_OPTIONS,
  },
  {
    name: "transfer_contains",
    label: `Type de transfert`,
    type: "select",
    useTranslation: true,
    client: "a9",
    table_name: "a9_transactions",
    language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
    translate_key: "transfer",
  },
  {
    name: "fee_type_contains",
    label: `Type de frais`,
    type: "select",
    useTranslation: true,
    client: "a9",
    table_name: "a9_transactions",
    language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
    translate_key: "fee_type",
  },
  {
    name: "description_contains",
    label: `Description`,
    type: "input",
  },
  {
    name: "date",
    type: "dateRange",
    label: "Date de valeur",
    from: { name: "date_from" },
    to: { name: "date_to" },
    col_span: 2,
  },
  {
    name: "create",
    type: "dateRange",
    label: "Date de création",
    from: { name: "create_from" },
    to: { name: "create_to" },
    col_span: 2,
  },
];

export function FilterA9Transactions() {
  return (
    <FilterTable
      columns={2}
      client="a9"
      schema={filterA9TransactionsSchema}
      tableName="a9_transactions"
      fields={fields}
    />
  );
}
